import { z } from "zod";

/**
 * The Apps template language: a tool call's input as literal values plus
 * `{ "$param": name }` references into the caller's params. Commands, behavior
 * service bindings and event payload mappings all use it, so it lives here
 * rather than in the backend manifest.
 */

export type TemplateValue =
  | string
  | number
  | boolean
  | null
  | { $param: string }
  | TemplateValue[]
  | { [key: string]: TemplateValue };

export const TemplateValueSchema: z.ZodType<TemplateValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.object({ $param: z.string().min(1) }).strict(),
    z.array(TemplateValueSchema),
    z.record(z.string(), TemplateValueSchema),
  ]),
);

/** A tool call's input: literal values and `{ "$param" }` references. */
export const ToolInputSchema = z.record(z.string(), TemplateValueSchema);
export type ToolInput = z.infer<typeof ToolInputSchema>;

export class MissingParamError extends Error {
  constructor(readonly param: string) {
    super(`Missing command parameter "${param}"`);
  }
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

export function isParamRef(value: TemplateValue): value is { $param: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 1 &&
    typeof value.$param === "string"
  );
}

/** Every `{ "$param": name }` a template reads. */
export function paramRefs(input: unknown): Set<string> {
  const names = new Set<string>();
  const visit = (value: TemplateValue): void => {
    if (isParamRef(value)) names.add(value.$param);
    else if (Array.isArray(value)) value.forEach(visit);
    else if (typeof value === "object" && value !== null)
      Object.values(value).forEach(visit);
  };
  if (input !== null && typeof input === "object")
    visit(input as TemplateValue);
  return names;
}

/** Resolve `{ "$param": name }` references against a params object. */
export function resolveTemplate(
  template: TemplateValue,
  params: Record<string, unknown>,
): unknown {
  if (isParamRef(template)) {
    // Own keys only: `constructor` or `toString` must not resolve to a function.
    if (!hasOwn(params, template.$param))
      throw new MissingParamError(template.$param);
    return params[template.$param];
  }
  if (Array.isArray(template))
    return template.map((item) => resolveTemplate(item, params));
  if (typeof template === "object" && template !== null) {
    return Object.fromEntries(
      Object.entries(template).map(([key, value]) => [
        key,
        resolveTemplate(value, params),
      ]),
    );
  }
  return template;
}

/** Resolve a whole template input against a params object. */
export function resolveInput(
  input: ToolInput,
  params: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(input).map(([key, value]) => [
      key,
      resolveTemplate(value, params),
    ]),
  );
}
